import isEmpty from 'lodash/isEmpty'

import config from '@acre/config'
import type { useFormatMessage } from '@acre/utils'

// A client has logged into CP when they have a signed CLIENT_PORTAL_IDD (verification_count > 0).
// Archived documents are excluded to avoid false positives from revoked/re-issued invites.
export const hasClientLoggedInToCp = (client: ClientVersion): boolean => {
  const signedCpIdd = client.details.miDocuments?.documents?.find(
    (document) =>
      document.template_type === 'CLIENT_PORTAL_IDD' &&
      !document.archived &&
      document.verification_count != null &&
      document.verification_count > 0,
  )
  return Boolean(signedCpIdd)
}

// Returns true only when every client has logged in — keeps button enabled if any client hasn't (AC5).
export const haveAllClientsLoggedInToCp = (clients: ClientVersion[]): boolean => {
  if (!clients || clients.length === 0) return false
  return clients.every(hasClientLoggedInToCp)
}

export const getClientInvitePayloads = (
  formatMessage: ReturnType<typeof useFormatMessage>,
  selectedClients?: string[],
  caseId?: Maybe<string>,
  user?: Maybe<User>,
) => {
  if (isEmpty(selectedClients) || !caseId || !user) return

  const clientPortalIddPayload: TemplateInput = {
    template_name: 'CLIENT_PORTAL_IDD',
    client_ids: selectedClients,
    case_ids: [caseId],
    organisation_ids: [user.organisation_id],
    store_document: true,
  }

  const organisationPrivacyPayload: TemplateInput = {
    template_name: 'ORGANISATION_PRIVACY_NOTICE',
    client_ids: selectedClients,
    organisation_ids: [user.organisation_id],
    store_document: true,
  }

  const creditReportReferralPayload: TemplateInput = {
    template_name: 'IDD',
    client_ids: selectedClients,
    organisation_id: config.ACRE_SUPPORT_ORG_ID,
  }

  const emailPayloads: EmailTemplateInput[] | undefined = selectedClients?.map((clientId) => {
    return {
      template: {
        template_name: 'WELCOME_EMAIL',
        client_ids: [clientId],
        organisation_ids: [user.organisation_id],
        user_ids: [user.id],
        case_ids: [caseId],
      },
      subject: formatMessage('caseOverview.clientInvitation.emailSubjectLine', {
        orgName: user.organisation?.name,
      }),
      client_recipient_ids: [clientId],
    }
  })

  return {
    emailPayloads,
    clientPortalIddPayload,
    organisationPrivacyPayload,
    creditReportReferralPayload,
  }
}
